"use client";

import { cn } from "@/lib/utils";
export type WeekDotState = "done" | "failed" | "missed" | "running" | "upcoming" | "off";
export interface WeekDot {
  key: string;
  /** The day and how it went, read out for the dot: "Fri, didn't finish". */
  label: string;
  state: WeekDotState;
  isToday?: boolean;
  /** The day's letter, shown over its dot so the week reads at a glance. */
  dayLetter?: string;
}
export const RUN_DOT_CLASS: Record<WeekDotState, string> = {
  done: "bg-dot-green",
  failed: "bg-dot-amber",
  missed: "bg-surface ring-1 ring-inset ring-dot-amber",
  running: "bg-cobalt shadow-halo",
  upcoming: "bg-surface shadow-control",
  off: "bg-ink/[0.08]"
};

/**
 * A week of something that repeats, one dot a day: done green, didn't finish
 * amber, missed an amber ring, working cobalt, still to come a grey ring, and
 * a faint dot on a day it doesn't run. Today's dot sits on a hairline; with
 * day letters, today's letter is the one in ink.
 */
export function WeekDots({
  days,
  label,
  className
}: {
  days: WeekDot[];
  /** What the week is of, as the list's accessible name. */
  label: string;
  className?: string;
}) {
  const hasLetters = days.some(day => day.dayLetter);
  return <ol aria-label={label} className={cn("m-0 flex list-none items-center p-0", hasLetters ? "gap-1.5" : "gap-1", className)} data-sentry-component="WeekDots" data-sentry-source-file="WeekDots.tsx">
            {days.map(day => <li key={day.key} aria-current={day.isToday ? "date" : undefined} className={cn("flex items-center justify-center", hasLetters ? "w-4 flex-col gap-1" : "h-4 w-3", day.isToday && !hasLetters && "border-b border-ink/30")}>
                    {hasLetters && <span aria-hidden className={cn("text-caption leading-none", day.isToday ? "font-medium text-ink" : "text-ink-3")}>
                            {day.dayLetter}
                        </span>}
                    <span title={day.label} className={cn("block rounded-full", hasLetters ? "h-2.5 w-2.5" : "h-2 w-2", RUN_DOT_CLASS[day.state])} />
                    <span className="sr-only">{day.label}</span>
                </li>)}
        </ol>;
}